import React, { useState } from 'react'
import dayjs from 'dayjs'
import FerryForm from './components/FerryForm'
import StayForm from './components/StayForm'
import AttractionsForm from './components/AttractionsForm'
import Tabs from './components/Tabs/Tabs'

import './styles/BookingWidget.scss'
import {
  defaultTabs,
  defaultTripTypes,
  defaultFerryOptions,
  defaultRoutes,
  passengerTypes,
} from './config'
import {
  convertFerryPassengersToHotelGuests,
  convertHotelGuestsToFerryPassengers,
  getDefaultFerryOptions,
  getTotalGuestCount,
  onCalendarOpen,
} from './utils'
import PassengerSelect from './components/PassengerSelect/PassengerSelect'
import Modal from './components/Modal'
import handleSubmit from './handleSubmit'
import HotelGuestSelect from './components/PassengerSelect/HotelGuestSelect'

const BookingWidget = ({
  widgetType = 'banner', // banner or card
  widgetOptions,
}) => {
  const [passengerModalIsOpen, setPassengerModalIsOpen] = useState(false)

  let hasValidCart = false

  try {
    if (widgetOptions?.checkCartStatus) {
      const storedCart = JSON.parse(localStorage.getItem('rfCartSelections'))
      const now = new Date().getTime()
      if (
        storedCart?.itemCount &&
        +storedCart.itemCount > 0 &&
        storedCart?.expires &&
        storedCart?.expires > now
      ) {
        hasValidCart = true
      }
    }
  } catch (e) {}

  const tripTypes = widgetOptions?.availableTripTypes || defaultTripTypes
  const availableTabs = widgetOptions?.tabs || defaultTabs
  // ferry options first
  const ferryOptions = widgetOptions?.availableFerries || defaultFerryOptions

  const outwardRoutes = widgetOptions?.availableOutwardRoutes || defaultRoutes
  const returnRoutes = widgetOptions?.availableReturnRoutes || defaultRoutes

  const formattedRouteOptions = ferryOptions.reduce((obj, ferry) => {
    const availableOutward = outwardRoutes.filter(
      route => route.ferryId === ferry.value,
    )
    const availableReturn = returnRoutes.filter(
      route => route.ferryId === ferry.value,
    )

    return {
      ...obj,
      [ferry.value]: {
        availableOutward,
        availableReturn,
      },
    }
  }, {})

  const offset = new Date().getTimezoneOffset()
  const todayInUK = dayjs()
    .add(offset + 60, 'minutes')
    .format('YYYY-MM-DD')

  const initialStartDate =
    widgetOptions?.startDate &&
    dayjs(widgetOptions?.startDate).isAfter(dayjs(todayInUK, 'days'))
      ? widgetOptions?.startDate
      : dayjs().format('YYYY-MM-DD')

  const initialEndDate =
    widgetOptions?.endDate &&
    (widgetOptions.endDate === initialStartDate ||
      dayjs(widgetOptions.endDate).isAfter(dayjs(initialStartDate), 'days'))
      ? widgetOptions.endDate
      : dayjs(initialStartDate).add(1, 'days')

  const initialEndDateAfter =
    widgetOptions?.endDate &&
    (widgetOptions.endDate === initialStartDate ||
      dayjs(widgetOptions.endDate).isAfter(dayjs(initialStartDate), 'days'))
      ? widgetOptions.endDate
      : dayjs(initialStartDate)

  const defaultPassengerType = passengerTypes.find(type => type.type === 'A')

  const { defaultFerryId, defaultDeparture, defaultReturn } =
    getDefaultFerryOptions(ferryOptions, formattedRouteOptions)

  const hidePromoField = !!widgetOptions?.hideCouponCode

  const [formValues, setFormValues] = useState({
    activeTab: availableTabs[0],
    journeyType: widgetOptions?.availableTripTypes?.[0]?.value || 'return',
    promoCode: widgetOptions?.couponCode || '',
    startDate: initialStartDate,
    endDate: initialEndDate,
    ferryId: defaultFerryId,
    departureRoute: defaultDeparture,
    returnRoute: defaultReturn === defaultDeparture ? '' : defaultReturn,
    passengers: {
      departure: {
        [defaultPassengerType.code]: {
          ...defaultPassengerType,
          count: 1,
        },
      },
      return: {
        [defaultPassengerType.code]: {
          ...defaultPassengerType,
          count: 1,
        },
      },
    },
    hasSetPassengers: false,
    hotelGuests: [
      {
        adults: 1,
        children: 0,
      },
    ],
  })
  const setActiveTab = tab => {
    setFormValues({
      ...formValues,
      activeTab: tab,
    })
  }
  const setJourneyType = type => {
    setFormValues({
      ...formValues,
      activeTab: 'ferry',
      journeyType: type,
    })
  }
  const setPromoCode = code => {
    setFormValues({
      ...formValues,
      promoCode: code,
    })
  }

  const passengerCount = getTotalGuestCount(formValues)

  const submit = () => {
    localStorage.removeItem('rfCartSelections')
    handleSubmit(formValues, {
      hidePromoField,
      campaignTitle: widgetOptions?.campaignTitle,
      campaignDescription: widgetOptions?.campaignDescription,
      submitInNewWindow: !!widgetOptions?.submitInNewWindow,
      productCode: widgetOptions?.productCode || null,
    })
  }
  const forms = {
    ferry: (
      <FerryForm
        ferryOptions={ferryOptions} // rf vs rj
        ferryRoutes={formattedRouteOptions} // outward and return route options
        journeyTypeOptions={tripTypes} // return vs one way
        onChangePromoCode={setPromoCode}
        promoCode={formValues.promoCode}
        hidePromoField={hidePromoField}
        widgetType={widgetType}
        onCalendarOpen={onCalendarOpen}
        values={formValues}
        setValues={setFormValues}
        passengerModalIsOpen={passengerModalIsOpen}
        setPassengerModalIsOpen={setPassengerModalIsOpen}
        passengerCount={passengerCount}
        submit={submit}
        hasValidCart={hasValidCart}
        initialEndDate={initialEndDateAfter}
      />
    ),
    stay: (
      <StayForm
        onChangePromoCode={setPromoCode}
        widgetType={widgetType}
        onCalendarOpen={onCalendarOpen}
        hidePromoField={hidePromoField}
        values={formValues}
        setValues={setFormValues}
        passengerModalIsOpen={passengerModalIsOpen}
        setPassengerModalIsOpen={setPassengerModalIsOpen}
        passengerCount={passengerCount}
        submit={submit}
        hasValidCart={hasValidCart}
        initialEndDate={initialEndDate}
      />
    ),
    attractions: (
      <AttractionsForm
        promoCode={formValues.promoCode}
        onChangePromoCode={setPromoCode}
        widgetType={widgetType}
        onCalendarOpen={onCalendarOpen}
        hidePromoField={hidePromoField}
        values={formValues}
        setValues={setFormValues}
        submit={submit}
        hasValidCart={hasValidCart}
        initialEndDate={initialEndDate}
      />
    ),
  }

  return (
    <div>
      <Tabs
        forms={forms}
        activeTab={formValues.activeTab}
        setActiveTab={setActiveTab}
        journeyType={formValues.journeyType}
        journeyTypeOptions={tripTypes}
        setJourneyType={setJourneyType}
        promoCode={formValues.promoCode}
        setPromoCode={setPromoCode}
        widgetType={widgetType}
        hidePromoField={!!widgetOptions?.hideCouponCode}
        availableTabs={availableTabs}
      />

      <Modal
        isOpen={passengerModalIsOpen}
        onRequestClose={() => setPassengerModalIsOpen(false)}
        widgetType={widgetType}
      >
        {!passengerModalIsOpen ? null : formValues.activeTab === 'ferry' ? (
          <PassengerSelect
            handleSubmit={newValues => {
              setFormValues({
                ...formValues,
                passengers: newValues,
                hasSetPassengers: true,
                hotelGuests: convertFerryPassengersToHotelGuests(
                  newValues?.departure,
                ),
              })
              setPassengerModalIsOpen(false)
            }}
            maxPassengers={widgetOptions?.maxPassengers || 7}
            isOneWay={formValues.journeyType === 'oneWay'}
            initialValues={formValues.passengers}
            submitText={'Update Passengers'}
            departurePassengerCodes={passengerTypes}
            renderCtas={({ submit: ctaSubmit }) => (
              <div className="passenger-select__buttons flex items-center justify-end pt-2">
                <button
                  type="button"
                  className="cancel px-9 py-4 text-base sm:text-lg text-red font-heading font-bold uppercase"
                  onClick={() => setPassengerModalIsOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="done btn-primary px-10 py-4 text-base sm:text-lg"
                  onClick={ctaSubmit}
                >
                  Done
                </button>
              </div>
            )}
          />
        ) : (
          <HotelGuestSelect
            initialValues={formValues.hotelGuests}
            handleSubmit={newValues => {
              const convertedToFerry = convertHotelGuestsToFerryPassengers({
                rooms: newValues,
              })

              setFormValues({
                ...formValues,
                hotelGuests: newValues,
                passengers: formValues.hasSetPassengers
                  ? formValues.passengers
                  : {
                      departure: convertedToFerry,
                      return: convertedToFerry,
                    },
              })
              setPassengerModalIsOpen(false)
            }}
            handleCancel={() => setPassengerModalIsOpen(false)}
            maxGuests={
              widgetOptions?.maxPassengers ||
              +process.env.REACT_APP_MAX_HOTEL_GUESTS
            }
          />
        )}
      </Modal>
    </div>
  )
}

export default BookingWidget
